/**
 * The seven request processors of LSEG's date functions (RDP.DateIsWD ...
 * RDP.DateFindDate), ported from Workspace's Excel add-in (Refinitiv for
 * Office 26.30.2). Each reads the Excel arguments in LSEG's order and builds
 * the request item LSEG sends to `analytics/functions/v1/common/<endpoint>`
 * (batched as `{ universe: [item] }` by the interface).
 *
 * As in LSEG, a blank Fields argument sends no `fields`: the processors have
 * no default list, only the names a user may ask for. A value that is falsy
 * (blank, 0, "") is left out of the request, as LSEG's `x && { key: x }` does.
 *
 * It also holds the function table of every ported AdFin function (the date
 * ones here, the FX ones from fxProcessors.ts) and `buildAdFinRequest`.
 */
import { AF_ARG, BaseDateProcessor } from "./baseProcessor";
import {
  FxDepToSwpProcessor,
  FxInfoProcessor,
  FxSpotProcessor,
  FxSwpToDepProcessor,
  FxSwpToSwpProcessor,
  FxZcToFraProcessor,
} from "./fxProcessors";
import { InterpProcessor, interpLocal } from "./interp";
import { AdFinValidationError, CellRange } from "./utils";

// LSEG: DateIsWDProcessor_47804.DateIsWDProcessor
export class DateIsWDProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["Calendars", "IsWorkingDay"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.calculationDate, AF_ARG.calendarCodes, AF_ARG.fields]);
  }

  // LSEG: DateIsWDProcessor_47804.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const calculationDate = this.getAdfinDate(
      this.getArg(AF_ARG.calculationDate),
      { calculationDate: "Calculation Date" },
      true,
    );
    const calendarCodes = this.getCalendarCodes(this.getArg(AF_ARG.calendarCodes));
    this.setOutputFields(this.getArg(AF_ARG.fields), DateIsWDProcessor.FIELDS, "Fields");
    return {
      ...(calculationDate && { calculationDate }),
      ...(calendarCodes && { calendarCodes }),
      ...(this.fields.length && { fields: this.fields }),
    };
  }
}

// LSEG: DateLastWDProcessor_58168.DateLastWDProcessor
export class DateLastWDProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["LastWorkingDay"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.calculationDate, AF_ARG.calendarCodes, AF_ARG.fields]);
  }

  // LSEG: DateLastWDProcessor_58168.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const calculationDate = this.getAdfinDate(
      this.getArg(AF_ARG.calculationDate),
      { calculationDate: "Calculation Date" },
      true,
    );
    const calendarCodes = this.getCalendarCodes(this.getArg(AF_ARG.calendarCodes));
    this.setOutputFields(this.getArg(AF_ARG.fields), DateLastWDProcessor.FIELDS, "Fields");
    return {
      ...(calculationDate && { calculationDate }),
      ...(calendarCodes && { calendarCodes }),
      ...(this.fields.length && { fields: this.fields }),
    };
  }
}

// LSEG: DateListHolidaysProcessor_75272.DateListHolidaysProcessor
export class DateListHolidaysProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["Date", "Name", "Calendars"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.startDate, AF_ARG.endDate, AF_ARG.calendarCodes, AF_ARG.fields]);
  }

  // LSEG: DateListHolidaysProcessor_75272.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const startDate = this.getAdfinDate(this.getArg(AF_ARG.startDate), { startDate: "Start Date" }, true);
    const endDate = this.getAdfinDate(this.getArg(AF_ARG.endDate), { endDate: "End Date" }, true);
    const calendarCodes = this.getCalendarCodes(this.getArg(AF_ARG.calendarCodes), true);
    this.setOutputFields(this.getArg(AF_ARG.fields), DateListHolidaysProcessor.FIELDS, "Fields");
    return {
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(calendarCodes && { calendarCodes }),
      ...(this.fields.length && { fields: this.fields }),
    };
  }
}

// LSEG: DateAdjToWDProcessor_39677.DateAdjToWDProcessor
export class DateAdjToWDProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["AdjustedDate"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.calculationDate, AF_ARG.calendarCodes, AF_ARG.dateMovingConvention, AF_ARG.fields]);
  }

  // LSEG: DateAdjToWDProcessor_39677.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const calculationDate = this.getAdfinDate(
      this.getArg(AF_ARG.calculationDate),
      { calculationDate: "Calculation Date" },
      true,
    );
    const calendarCodes = this.getCalendarCodes(this.getArg(AF_ARG.calendarCodes));
    const dateMovingConvention = this.getDateMovingConvention(this.getArg(AF_ARG.dateMovingConvention));
    this.setOutputFields(this.getArg(AF_ARG.fields), DateAdjToWDProcessor.FIELDS, "Fields");
    return {
      ...(calculationDate && { calculationDate }),
      ...(calendarCodes && { calendarCodes }),
      ...(dateMovingConvention && { dateMovingConvention }),
      ...(this.fields.length && { fields: this.fields }),
    };
  }
}

// LSEG: DateCalcPeriodProcessor_87772.DateCalcPeriodProcessor
export class DateCalcPeriodProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["StartDate", "EndDate"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([
      AF_ARG.calculationDate,
      AF_ARG.periods,
      AF_ARG.calendarCodes,
      AF_ARG.currencyCodes,
      AF_ARG.fields,
      AF_ARG.dateMovingConvention,
    ]);
  }

  // LSEG: DateCalcPeriodProcessor_87772.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const calculationDate = this.getAdfinDate(
      this.getArg(AF_ARG.calculationDate),
      { calculationDate: "Calculation Date" },
      true,
    );
    const period = this.getPeriods();
    const calendarCodes = this.getCalendarCodes(this.getArg(AF_ARG.calendarCodes));
    const currencyCodes = this.getCurrencyCodes(this.getArg(AF_ARG.currencyCodes));
    this.adFinUtils.validate(
      !(calendarCodes && currencyCodes),
      "Calendar/Currency codes",
      "use Calendar OR Currency codes, not both",
    );
    this.setOutputFields(this.getArg(AF_ARG.fields), DateCalcPeriodProcessor.FIELDS, "Fields");
    const dateMovingConvention = this.getDateMovingConvention(this.getArg(AF_ARG.dateMovingConvention));
    return {
      ...(calculationDate && { calculationDate }),
      ...(period && { period }),
      ...(calendarCodes && { calendarCodes }),
      ...(currencyCodes && { currencyCodes }),
      ...(this.fields.length && { fields: this.fields }),
      ...(dateMovingConvention && { dateMovingConvention }),
    };
  }

  // LSEG: DateCalcPeriodProcessor_87772.getPeriods
  private getPeriods(): string | undefined {
    return this.getPeriod(this.getArg(AF_ARG.periods), { period: "Period" }, true);
  }
}

// LSEG: DateCountPeriodsProcessor_34184.DateCountPeriodsProcessor
export class DateCountPeriodsProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["Count", "Tenor"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([
      AF_ARG.startDate,
      AF_ARG.endDate,
      AF_ARG.calendarCodes,
      AF_ARG.periodType,
      AF_ARG.dayCountBasis,
      AF_ARG.fields,
    ]);
  }

  // LSEG: DateCountPeriodsProcessor_34184.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const startDate = this.getAdfinDate(this.getArg(AF_ARG.startDate), { startDate: "Start Date" }, true);
    const endDate = this.getAdfinDate(this.getArg(AF_ARG.endDate), { endDate: "End Date" }, true);
    const calendarCodes = this.getCalendarCodes(this.getArg(AF_ARG.calendarCodes), false);
    const periodType = this.getPeriodType(this.getArg(AF_ARG.periodType));
    const dayCountBasis = this.getDayCountBasis(this.getArg(AF_ARG.dayCountBasis));
    this.setOutputFields(this.getArg(AF_ARG.fields), DateCountPeriodsProcessor.FIELDS, "Fields");
    return {
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(calendarCodes && { calendarCodes }),
      ...(periodType && { periodType }),
      ...(dayCountBasis && { dayCountBasis }),
      ...(this.fields.length && { fields: this.fields }),
    };
  }

  /** One of WorkingDay, NonWorkingDay, Day, Year, from a text or a range. */
  // LSEG: DateCountPeriodsProcessor_34184.getPeriodType
  private getPeriodType(value: unknown): string | undefined {
    const fieldName = "Period Type";
    if (!value) return undefined;
    const allowed = this.adFinUtils.listToObject(["WorkingDay", "NonWorkingDay", "Day", "Year"]);
    const names = Array.isArray(value)
      ? this.aFCellRangeProcessor.processCellRangeArgument(value as CellRange, allowed, fieldName)
      : this.extractAdfinArgument(value, allowed, fieldName);
    this.adFinUtils.validate(names.length === 1, fieldName, "not in a valid format");
    return names[0];
  }
}

/** `{ Monday: "Mon" }` -> `{ monday: "Mon" }`. */
// LSEG: DateFindDateProcessor_94722 (its key-lowering helper)
function lowerKeys(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  Object.keys(map).forEach((k) => {
    out[k.toLowerCase()] = map[k];
  });
  return out;
}

// LSEG: DateFindDateProcessor_94722 (its day names)
const FIND_DATE_DAYS = lowerKeys({
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
  Mon: "Mon",
  Tue: "Tue",
  Wed: "Wed",
  Thu: "Thu",
  Fri: "Fri",
  Sat: "Sat",
  Sun: "Sun",
});
// LSEG: DateFindDateProcessor_94722 (its direction names)
const FIND_DATE_DIRECTIONS = lowerKeys({ Before: "Before", After: "After", Of: "Before", Bef: "Before", Aft: "After" });

/** LSEG's spelling of `name` (any case) in `map`, or "" when it has none. */
// LSEG: DateFindDateProcessor_94722 (its lookup helper)
function lookUp(name: string, map: Record<string, string>): string {
  return map[name.toLowerCase()] ?? "";
}

type ArgumentField<T> = {
  argument: unknown;
  fieldName: string;
  formatValue: (value: unknown) => T;
  validateValue: (value: T) => boolean;
  isMandatory: boolean;
};

// LSEG: DateFindDateProcessor_94722.DateFindDateProcessor
export class DateFindDateProcessor extends BaseDateProcessor {
  static readonly FIELDS = ["Date"];

  constructor(args: unknown[]) {
    super(args);
    this.setArgIndexes([AF_ARG.dayNumber, AF_ARG.day, AF_ARG.direction, AF_ARG.referenceDate, AF_ARG.fields]);
  }

  // LSEG: DateFindDateProcessor_94722.prepareRequestInput
  prepareRequestInput(): Record<string, unknown> {
    const dayNumber = this.getDayNumber(this.getArg(AF_ARG.dayNumber), true);
    const day = this.getDay(this.getArg(AF_ARG.day), true);
    const direction = this.getDirection(this.getArg(AF_ARG.direction), true);
    const referenceDate = this.getAdfinDate(
      this.getArg(AF_ARG.referenceDate),
      { referenceDate: "Reference Date" },
      false,
    );
    this.setOutputFields(this.getArg(AF_ARG.fields), DateFindDateProcessor.FIELDS, "Fields");
    return {
      ...(dayNumber && { dayNumber }),
      ...(day && { day }),
      ...(direction && { direction }),
      ...(referenceDate && { referenceDate }),
      ...(this.fields.length && { fields: this.fields }),
    };
  }

  /** A number, read as JavaScript's unary plus reads it. */
  // LSEG: DateFindDateProcessor_94722.getDayNumber
  private getDayNumber(argument: unknown, isMandatory: boolean): number {
    return this.getArgumentField({
      argument,
      isMandatory,
      fieldName: "Day Number",
      formatValue: (v) => Number(v),
      validateValue: (n) => !isNaN(n),
    });
  }

  /** Monday or Mon (any case) -> "Mon". */
  // LSEG: DateFindDateProcessor_94722.getDay
  private getDay(argument: unknown, isMandatory: boolean): string {
    return this.getArgumentField({
      argument,
      isMandatory,
      fieldName: "Day",
      formatValue: (v) => (typeof v === "string" ? lookUp(v, FIND_DATE_DAYS) : ""),
      validateValue: (s) => Boolean(s),
    });
  }

  /** Before, After, Of, Bef, Aft (any case) -> "Before" or "After". */
  // LSEG: DateFindDateProcessor_94722.getDirection
  private getDirection(argument: unknown, isMandatory: boolean): string {
    return this.getArgumentField({
      argument,
      isMandatory,
      fieldName: "Direction",
      formatValue: (v) => (typeof v === "string" ? lookUp(v, FIND_DATE_DIRECTIONS) : ""),
      validateValue: (s) => Boolean(s),
    });
  }

  /** One value from a range, a "Label:value" text, or the value itself, formatted then checked. */
  // LSEG: DateFindDateProcessor_94722.getArgumentField
  private getArgumentField<T>({ argument, fieldName, formatValue, validateValue, isMandatory }: ArgumentField<T>): T {
    if (isMandatory) this.validateNotEmpty(argument, fieldName);
    const read = (value: unknown): T => {
      const formatted = formatValue(value);
      this.adFinUtils.validate(validateValue(formatted), fieldName, "not in the right format");
      return formatted;
    };
    if (Array.isArray(argument)) {
      return this.aFCellRangeProcessor.processCellRangeSingleValueParam(argument as CellRange, fieldName, read);
    }
    if (typeof argument === "string" && argument.includes(":")) {
      const [label, value] = argument.split(":");
      this.adFinUtils.validateLabel(label, fieldName);
      return read(value);
    }
    return read(argument);
  }
}

/** The seven date functions, by LSEG's Excel name without "RDP.". */
export type DateFn =
  | "DateIsWD"
  | "DateAdjToWD"
  | "DateLastWD"
  | "DateListHolidays"
  | "DateCalcPeriod"
  | "DateCountPeriods"
  | "DateFindDate";

/** The six FX functions, by LSEG's Excel name without "RDP.". */
export type FxFn = "FxSpot" | "FxSwpToSwp" | "FxSwpToDep" | "FxDepToSwp" | "FxZcToFra" | "FxInfo";

/** Every AdFin function EAV ports: the date and FX ones, and RDP.Interp (answered here, not by the interface). */
export type AdFinFn = DateFn | FxFn | "Interp";

export type DateFunctionSpec = {
  /** LSEG's request processor for the function. */
  processor: new (args: unknown[]) => BaseDateProcessor;
  /**
   * The function's name at the hub: under `analytics/functions/v1/common/`
   * for the dates, `fx/` for the FX functions, `rates/` for zc-to-fra.
   */
  endpoint: string;
  /** LSEG's Excel argument names, in order, Options and Destination Cell included (lseg_functions.json, trimmed). */
  argNames: readonly string[];
  /**
   * A function the add-in answers itself: its request goes to this instead of the
   * interface, and it returns the raw answer the response processor reads (LSEG's
   * executor result, or `{ eavText }` for a text to show as is).
   */
  local?: (request: Record<string, unknown>) => unknown;
};

const OPTIONS_AND_DEST = ["Options", "Destination Cell"];

/** RDP.<DateFn> -> its processor, endpoint and Excel arguments. */
// LSEG: the AdFinSubType.date* entries of its processors map and batched endpoints (hostProd)
export const ADFIN_DATE_FUNCTIONS: Record<DateFn, DateFunctionSpec> = {
  DateIsWD: {
    processor: DateIsWDProcessor,
    endpoint: "is-working-day",
    argNames: ["Calculation date", "Calendar codes", "Fields", ...OPTIONS_AND_DEST],
  },
  DateAdjToWD: {
    processor: DateAdjToWDProcessor,
    endpoint: "adjust-to-working-day",
    argNames: ["Calculation date.", "Calendar Codes", "Date moving convention", "Fields", ...OPTIONS_AND_DEST],
  },
  DateLastWD: {
    processor: DateLastWDProcessor,
    endpoint: "last-working-day",
    argNames: ["Calculation Date", "Calendar codes", "Fields", ...OPTIONS_AND_DEST],
  },
  DateListHolidays: {
    processor: DateListHolidaysProcessor,
    endpoint: "list-holidays",
    argNames: ["Start Date", "End Date", "Calendar Codes", "Fields", ...OPTIONS_AND_DEST],
  },
  DateCalcPeriod: {
    processor: DateCalcPeriodProcessor,
    endpoint: "add-period",
    argNames: [
      "Calculation date",
      "Period",
      "Calendar codes",
      "Currency codes",
      "Fields",
      "Date moving convention",
      ...OPTIONS_AND_DEST,
    ],
  },
  DateCountPeriods: {
    processor: DateCountPeriodsProcessor,
    endpoint: "count-periods",
    argNames: [
      "Start date",
      "End date",
      "Calendar codes",
      "Period types",
      "Day Count Basis",
      "Fields",
      ...OPTIONS_AND_DEST,
    ],
  },
  DateFindDate: {
    processor: DateFindDateProcessor,
    endpoint: "find-date",
    argNames: ["Day Number", "Day", "Direction", "Reference Date", "Fields", ...OPTIONS_AND_DEST],
  },
};

/** RDP.<FxFn> -> its processor, endpoint and Excel arguments. */
// LSEG: the AdFinSubType spot, swapToSwap, swapToDep, depToSwap, zcToFra and info entries of its
// processors map and batched endpoints (hostProd)
export const ADFIN_FX_FUNCTIONS: Record<FxFn, DateFunctionSpec> = {
  FxSpot: {
    processor: FxSpotProcessor,
    endpoint: "fx-spot",
    argNames: ["fxCrossCode", "marketDataDate", "spotCcy1", "spotCcy2", "Fields", ...OPTIONS_AND_DEST],
  },
  FxSwpToSwp: {
    processor: FxSwpToSwpProcessor,
    endpoint: "swap-to-swap",
    argNames: [
      "fxCrossCode",
      "marketDataDate",
      "tenors",
      "spotCcy1",
      "spotCcy2",
      "swapPointsCcy1",
      "swapPointsCcy2",
      "Fields",
      ...OPTIONS_AND_DEST,
    ],
  },
  FxSwpToDep: {
    processor: FxSwpToDepProcessor,
    endpoint: "swap-to-dep",
    argNames: [
      "fxCrossCode",
      "marketDataDate",
      "tenors",
      "spotCcy1",
      "spotCcy2",
      "swapPointsCcy1",
      "swapPointsCcy2",
      "depoRatesCcy1",
      "Fields",
      ...OPTIONS_AND_DEST,
    ],
  },
  FxDepToSwp: {
    processor: FxDepToSwpProcessor,
    endpoint: "dep-to-swap",
    argNames: [
      "fxCrossCode",
      "marketDataDate",
      "tenors",
      "spotCcy1",
      "spotCcy2",
      "depRatesCcy1",
      "depRatesCcy2",
      "Fields",
      ...OPTIONS_AND_DEST,
    ],
  },
  FxZcToFra: {
    processor: FxZcToFraProcessor,
    endpoint: "zc-to-fra",
    argNames: ["zcCurve", "Start", "End", "Calculation Date", "Day Count Basis", "Fields", ...OPTIONS_AND_DEST],
  },
  FxInfo: {
    processor: FxInfoProcessor,
    endpoint: "fx-info",
    argNames: ["Currency codes", "Fields", ...OPTIONS_AND_DEST],
  },
};

/** RDP.Interp: computed by `interpLocal`, so it has no endpoint at the hub. */
// LSEG: the AdFinSubType.interp entry of its processors map (its executor, 54902, calls Adfin's AdInterp inside the add-in)
export const ADFIN_INTERP_FUNCTION: DateFunctionSpec = {
  processor: InterpProcessor,
  endpoint: "",
  argNames: ["x", "X values", "Y values", "Interpolation method", ...OPTIONS_AND_DEST],
  local: interpLocal,
};

/** Every ported AdFin function: the seven date functions, the six FX ones and Interp. */
export const ADFIN_FUNCTIONS: Record<AdFinFn, DateFunctionSpec> = {
  ...ADFIN_DATE_FUNCTIONS,
  ...ADFIN_FX_FUNCTIONS,
  Interp: ADFIN_INTERP_FUNCTION,
};

/**
 * The request LSEG would send for `RDP.<fn>(...args)`, or its validation
 * text. `args` are the Excel arguments in LSEG's order (the function's own,
 * then Options, then Dest); they are not changed. Errors that are not
 * validation errors are not caught.
 */
export function buildAdFinRequest(
  fn: AdFinFn,
  args: unknown[],
): { endpoint: string; request: Record<string, unknown> } | { error: string } {
  const spec = ADFIN_FUNCTIONS[fn];
  try {
    return { endpoint: spec.endpoint, request: new spec.processor(args).getAdFinRequestInput() };
  } catch (e) {
    if (e instanceof AdFinValidationError) return { error: e.message };
    throw e;
  }
}

/** The name buildAdFinRequest had when it built the date requests only. */
export const buildDateRequest = buildAdFinRequest;
